interface LogEntry {
  level: "debug" | "info" | "warn" | "error";
  message: string;
  source?: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

const buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function enqueue(entry: LogEntry) {
  buffer.push(entry);

  if (import.meta.env.DEV) {
    const fn =
      entry.level === "error"
        ? console.error
        : entry.level === "warn"
          ? console.warn
          : console.debug;
    fn(`[${entry.source}]`, entry.message, entry.data ?? "");
  }

  if (buffer.length >= 20) {
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, 5000);
  }
}

async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0) return;

  const entries = buffer.splice(0, 50);
  try {
    await fetch("/api/logs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entries }),
      credentials: "include",
    });
  } catch {
    // Silently drop — logging should never crash the app
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (buffer.length > 0) {
      navigator.sendBeacon(
        "/api/logs",
        JSON.stringify({ entries: buffer.splice(0) }),
      );
    }
  });
}

export const logger = {
  debug: (
    message: string,
    data?: Record<string, unknown>,
    source = "editor",
  ) =>
    enqueue({
      level: "debug",
      message,
      source,
      data,
      timestamp: new Date().toISOString(),
    }),

  info: (
    message: string,
    data?: Record<string, unknown>,
    source = "editor",
  ) =>
    enqueue({
      level: "info",
      message,
      source,
      data,
      timestamp: new Date().toISOString(),
    }),

  warn: (
    message: string,
    data?: Record<string, unknown>,
    source = "editor",
  ) =>
    enqueue({
      level: "warn",
      message,
      source,
      data,
      timestamp: new Date().toISOString(),
    }),

  error: (
    message: string,
    data?: Record<string, unknown>,
    source = "editor",
  ) =>
    enqueue({
      level: "error",
      message,
      source,
      data,
      timestamp: new Date().toISOString(),
    }),
};
